import { AccessData } from '$core/services/auth.service';
import { Card } from '$modules/personal/cards/card';
import { BelpostLegalInfo } from '$core/models/belpost-adapted-legal-info';

export enum UserTypes {
  Individual = 'individual',
  Legal = 'legal',
}

export const isUserType = (val: any): val is UserTypes => {
  return Object.values(UserTypes).includes(val);
};

export enum IdentificationStatus {
  Approved = 'approved',
  Nope = 'nope',
  Declined = 'declined',
  Expired = 'expired',
  Pending = 'pending',
  Renewed = 'renewed',
}

export const UserTypesMap = new Map([
  [UserTypes.Individual, 'INDIVIDUAL'],
  [UserTypes.Legal, 'LEGAL'],
]);

export class User {
  public default_address: any; // BelpostRepublicAddress; | null
  public default_card: Card;
  public default_legal_info: BelpostLegalInfo | null;
  public id: number;
  public name: string;
  public has_publications?: boolean;
  public trn: string;
  public email: string;
  public email_verification_in_progress: string | null;
  public phone?: any;
  public type: UserTypes;
  public email_verified_at: string;
  public phone_verified_at: string;
  public email_notification_enabled: 1 | 0 | boolean;
  public sms_notification_enabled: 1 | 0 | boolean;
  public has_virtual_subscriptions: boolean;
  public accept_paper_offer_at?: string;
  public accept_virtual_offer_at?: string;
  public filial: string | null;
  public corporate_card_short_company_name?: string;
  public is_legal_info_exist?: boolean;
  public first_name: string;
  public second_name: string;
  public last_name: string;
  public identification_status?: IdentificationStatus;
  public is_type_individual: boolean;
  public is_accept_advertisement: 0 | 1;
  public is_agree_on_individual_card: boolean;
  public is_accept_terms: boolean;
  public individual_card_access?: InvididualCardAccess;

  constructor(user) {
    if (user) {
      Object.assign(this, user);
    }
    if (this.default_card) {
      this.default_card = new Card(this.default_card);
    }
  }

  public get hasLegalDataForBatches(): boolean {
    return (
      !!this.trn &&
      !!this.name &&
      !!this.default_legal_info &&
      !!this.default_legal_info.bank_name &&
      !!this.default_legal_info.bank_address &&
      !!this.default_legal_info.checking_account
    );
  }

  public get reasonsToUpdateIdentification(): readonly IndividualCardUnavailabilityReason[] {
    const allReasons = this.individual_card_access?.reasons;

    if (!allReasons) return [];

    return getIndividualCardDataUpdateReasons(allReasons);
  }
}

export interface UserWithToken extends User {
  token: AccessData;
}

export interface InvididualCardAccess {
  readonly is_available: boolean;

  readonly reasons: readonly IndividualCardUnavailabilityReason[];
}

export interface IndividualCardUnavailabilityReason {
  readonly key: IndividualCardUnavailabilityReasonKey;
}

export type IndividualCardUnavailabilityReasonKey =
  | 'identification_not_approved'
  | 'doc_type_not_allowed'
  | 'doc_personal_number_invalid'
  | 'birth_date_missing'
  | 'second_name_empty'
  | 'fio_not_cyrillic'
  | 'public_offer_not_accepted'
  | 'payment_system_agreement_not_accepted';

const INDIVIDUAL_CARD_DATA_UPDATE_REASON_KEYS: readonly IndividualCardUnavailabilityReasonKey[] =
  [
    'doc_type_not_allowed',
    'doc_personal_number_invalid',
    'birth_date_missing',
    'second_name_empty',
    'fio_not_cyrillic',
  ];

function getIndividualCardDataUpdateReasons(
  reasons: readonly IndividualCardUnavailabilityReason[] = []
): readonly IndividualCardUnavailabilityReason[] {
  return reasons.filter((reason) =>
    INDIVIDUAL_CARD_DATA_UPDATE_REASON_KEYS.includes(reason.key)
  );
}
